// Per-banner overlay text (title / subtitle / optional CTA button) for the storefront
// banner carousel. Persisted as ONE opaque JSON string on IStorefrontBanner.overlayConfig
// so the backend only needs a single new field. Style shapes are shared with the search
// page config (fontSize is a PERCENT number rendered as `${n}%`).

import React from 'react';
import { ISearchElementStyle, ISearchTextStyle } from './searchPageConfig';
import { elementStyleFrom } from './styleControls';

export type BannerOverlayHorizontal = 'left' | 'center' | 'right';
export type BannerOverlayVertical = 'top' | 'middle' | 'bottom';

export interface IBannerOverlayPosition {
    horizontal?: BannerOverlayHorizontal;   // default 'left'
    vertical?: BannerOverlayVertical;       // default 'middle'
    maxWidth?: number;                      // percent of banner width, default 50
    textAlign?: BannerOverlayHorizontal;    // default follows horizontal
}

export interface IBannerOverlayButton {
    label?: string;
    url?: string;                           // falls back to banner.linkUrl when empty
    style?: ISearchElementStyle;
    // Hover/focus highlight, merged OVER `style` while the pointer is on the button (and while it is
    // keyboard-focused), so it only needs the properties that change — a brighter backgroundColor, a
    // glow boxShadow, a borderColor. Absent ⇒ the storefront's color-agnostic default
    // (brighten + lift + shadow).
    hoverStyle?: ISearchElementStyle;
}

export interface IBannerOverlayConfig {
    title?: string;
    titleStyle?: ISearchTextStyle;
    subtitle?: string;
    subtitleStyle?: ISearchTextStyle;
    button?: IBannerOverlayButton;
    position?: IBannerOverlayPosition;
    // Backdrop box around the whole text stack (title/subtitle/button): background, border,
    // radius, and padding (the extra space around the text).
    box?: ISearchElementStyle;
    // 0-100 opacity applied to the box's backgroundColor ONLY (combined into rgba at render
    // time — CSS `opacity` on the box would fade the text too). Absent = fully opaque.
    boxOpacity?: number;
}

// An overlay only renders (and only persists) when it has visible content.
export const hasBannerOverlayContent = (config: IBannerOverlayConfig): boolean =>
    !!(config.title?.trim() || config.subtitle?.trim() || config.button?.label?.trim());

export const parseBannerOverlayConfig = (raw?: string): IBannerOverlayConfig => {
    if (!raw) return {};
    try {
        return JSON.parse(raw) as IBannerOverlayConfig;
    } catch {
        return {};
    }
};

// Undefined when effectively empty (no title, subtitle, or button label) so untouched
// banners don't grow an overlayConfig field.
export const serializeBannerOverlayConfig = (config: IBannerOverlayConfig): string | undefined =>
    hasBannerOverlayContent(config) ? JSON.stringify(config) : undefined;

// ── Overlay layout builders ──────────────────────────────────────────────────────────────
// Ported from the storefront runtime (ac-storefront-web Components/Helpers/bannerOverlayConfig).
// The preview used to hand-roll one layout for every case, which is why a scaled overlay sat in the
// wrong place at the wrong size: the runtime picks between THREE layouts and only one of them
// matches what the editor was drawing.

const horizontalJustify = (h?: BannerOverlayHorizontal): React.CSSProperties['alignItems'] =>
    h === 'center' ? 'center' : h === 'right' ? 'flex-end' : 'flex-start';

const verticalAlign = (v?: BannerOverlayVertical): React.CSSProperties['justifyContent'] =>
    v === 'top' ? 'flex-start' : v === 'bottom' ? 'flex-end' : 'center';

// The absolutely-positioned overlay layer that fills the banner and carries the positioning.
// Used when the banner is NOT full-bleed. pointer-events:none lets clicks fall through to the
// banner; the CTA re-enables them on itself.
export const overlayLayerStyleFrom = (position?: IBannerOverlayPosition): React.CSSProperties => ({
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: verticalAlign(position?.vertical),
    alignItems: horizontalJustify(position?.horizontal),
    padding: '24px',
    boxSizing: 'border-box',
    pointerEvents: 'none',
});

// The bare absolute layer used when the banner IS full-bleed: it only fills the banner and lets
// clicks through. The positioning lives on one of the two frames below instead, so the overlay
// aligns to the page content column rather than to the full-bleed image.
export const overlayFramedLayerStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
};

// Full-bleed WITHOUT overlay-scale: constrain the overlay to the page content width and center it,
// carrying the same flex positioning the plain layer would.
export const overlayFrameStyleFrom = (
    position: IBannerOverlayPosition | undefined,
    maxWidthPx: number,
): React.CSSProperties => ({
    width: '100%',
    height: '100%',
    maxWidth: `${maxWidthPx}px`,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: verticalAlign(position?.vertical),
    alignItems: horizontalJustify(position?.horizontal),
    padding: '24px',
    boxSizing: 'border-box',
    pointerEvents: 'none',
});

// Full-bleed WITH overlay-scale (homePageConfig.bannerOverlayScale): everything is expressed in
// container-query width units (cqw = 1% of the banner's width), so the overlay's position AND size
// track the banner proportionally instead of locking to pixels.
//
// `fontSize: 1cqw` is the load-bearing line: merchants size the CTA and headings in `em`, so this is
// what those em values resolve against. Without a query container the em falls back to the inherited
// ~14px and the whole overlay renders roughly a third of its real size — which is exactly what the
// preview was doing. The banner container must set `containerType: 'inline-size'` for cqw to resolve.
export const overlayScaledFrameStyle = (position?: IBannerOverlayPosition): React.CSSProperties => {
    const h = position?.horizontal ?? 'left';
    // A left-anchored CTA sits under a left headline (20cqw ~= the baked content x); center/right
    // use a small symmetric inset so the CTA centers under a centered headline.
    return {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: verticalAlign(position?.vertical ?? 'bottom'),
        alignItems: horizontalJustify(h),
        paddingLeft: h === 'left' ? '20cqw' : '4cqw',
        paddingRight: h === 'right' ? '20cqw' : '4cqw',
        paddingTop: '4cqw',
        paddingBottom: '2.6cqw',
        boxSizing: 'border-box',
        pointerEvents: 'none',
        fontSize: '1cqw',
    };
};

// The inner content column (title / subtitle / CTA stack) shared by all three layouts.
export const overlayContentStyleFrom = (position?: IBannerOverlayPosition): React.CSSProperties => ({
    maxWidth: `${position?.maxWidth ?? 50}%`,
    textAlign: (position?.textAlign ?? position?.horizontal ?? 'left') as React.CSSProperties['textAlign'],
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    alignItems: horizontalJustify(position?.horizontal),
});

// Apply an opacity (0-100) to a solid CSS color. Handles #rgb/#rrggbb and rgb(...);
// anything else (named colors, existing rgba) is returned unchanged.
const withAlpha = (color: string, opacityPct: number): string => {
    const a = Math.max(0, Math.min(100, opacityPct)) / 100;
    const c = color.trim();
    const m3 = /^#([0-9a-f]{3})$/i.exec(c);
    if (m3) {
        const [r, g, b] = m3[1].split('').map((h) => parseInt(h + h, 16));
        return `rgba(${r}, ${g}, ${b}, ${a})`;
    }
    const m6 = /^#([0-9a-f]{6})$/i.exec(c);
    if (m6) {
        const v = m6[1];
        return `rgba(${parseInt(v.slice(0, 2), 16)}, ${parseInt(v.slice(2, 4), 16)}, ${parseInt(v.slice(4, 6), 16)}, ${a})`;
    }
    const rgb = /^rgb\(([^)]+)\)$/i.exec(c);
    if (rgb) return `rgba(${rgb[1]}, ${a})`;
    return c;
};

// Whether a CSS padding value is effectively zero ("0", "0px", "0 0", 0, "0em 0px", ...).
const isZeroPadding = (p: unknown): boolean => {
    const s = String(p).trim();
    if (s === '') return true;
    return s.split(/\s+/).every((part) => parseFloat(part) === 0);
};

const PADDING_KEYS = ['padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft'] as const;

// CSS for the backdrop box on the overlay content column. boxOpacity folds into the
// backgroundColor as rgba so the text/button on top stay fully opaque.
//
// Mirrors the storefront renderer's default-padding rule so the preview stays WYSIWYG: a
// VISIBLE backdrop (background, border or shadow) with no padding, or only zero padding,
// renders with 1em. An invisible box is left exactly as configured.
export const overlayBoxStyleFrom = (config: IBannerOverlayConfig): React.CSSProperties => {
    const css = elementStyleFrom(config.box);
    if (config.boxOpacity != null && css.backgroundColor) {
        css.backgroundColor = withAlpha(String(css.backgroundColor), config.boxOpacity);
    }
    const visible = !!(css.backgroundColor || css.background || css.border || css.borderColor
        || css.borderWidth || css.boxShadow || css.backgroundImage);
    const provided = PADDING_KEYS.filter((k) => css[k] != null);
    if (visible && provided.every((k) => isZeroPadding(css[k]))) {
        provided.forEach((k) => delete css[k]);
        css.padding = '1em';
    }
    return css;
};
